import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Loading skeleton components
 *
 * Skeletons provide a better UX than spinners by:
 * 1. Reducing perceived loading time
 * 2. Preventing layout shifts
 * 3. Providing visual context for incoming content
 */

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.secondary} 0%,
    ${({ theme }) => theme.colors.surface} 50%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ $borderRadius, theme }) => $borderRadius || theme.borderRadius.sm};
`;

/**
 * Basic skeleton block
 */
export const SkeletonBlock = styled(SkeletonBase)`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '20px'};
  margin-bottom: ${({ $mb, theme }) => $mb || theme.spacing.sm};
`;

/**
 * Text line skeleton
 */
export const SkeletonText = styled(SkeletonBase)`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $size }) => {
    switch ($size) {
      case 'sm': return '12px';
      case 'lg': return '24px';
      case 'xl': return '32px';
      default: return '16px';
    }
  }};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

/**
 * Circle skeleton (avatars, icons)
 */
export const SkeletonCircle = styled(SkeletonBase)`
  width: ${({ $size }) => $size || '40px'};
  height: ${({ $size }) => $size || '40px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

/**
 * StatCard Skeleton
 */
const StatCardSkeletonWrapper = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  min-height: 120px;
`;

const StatCardSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StatCardSkeleton = memo(() => (
  <StatCardSkeletonWrapper>
    <StatCardSkeletonHeader>
      <SkeletonCircle $size="48px" />
    </StatCardSkeletonHeader>
    <SkeletonText $width="60%" $size="xl" />
    <SkeletonText $width="80%" $size="sm" />
  </StatCardSkeletonWrapper>
));

StatCardSkeleton.displayName = 'StatCardSkeleton';

/**
 * DataCard Skeleton
 */
const DataCardSkeletonWrapper = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  min-height: ${({ $minHeight }) => $minHeight || '200px'};
`;

const DataCardSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DataCardSkeletonBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

export const DataCardSkeleton = memo(({ rows = 4, minHeight = '200px' }) => (
  <DataCardSkeletonWrapper $minHeight={minHeight}>
    <DataCardSkeletonHeader>
      <SkeletonText $width="150px" $size="lg" />
      <SkeletonCircle $size="24px" />
    </DataCardSkeletonHeader>
    <DataCardSkeletonBody>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonText
          key={i}
          $width={`${70 + Math.random() * 30}%`}
          style={{ marginBottom: 12 }}
        />
      ))}
    </DataCardSkeletonBody>
  </DataCardSkeletonWrapper>
));

DataCardSkeleton.displayName = 'DataCardSkeleton';
DataCardSkeleton.propTypes = {
  rows: PropTypes.number,
  minHeight: PropTypes.string,
};

/**
 * Table Row Skeleton
 */
const TableRowSkeletonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const TableRowSkeleton = memo(({ columns = 4 }) => (
  <TableRowSkeletonWrapper>
    {Array.from({ length: columns }).map((_, i) => (
      <SkeletonText
        key={i}
        $width={`${100 / columns - 5}%`}
        style={{ marginBottom: 0 }}
      />
    ))}
  </TableRowSkeletonWrapper>
));

TableRowSkeleton.displayName = 'TableRowSkeleton';
TableRowSkeleton.propTypes = {
  columns: PropTypes.number,
};

/**
 * Table Skeleton
 */
export const TableSkeleton = memo(({ rows = 5, columns = 4 }) => (
  <div>
    <TableRowSkeletonWrapper style={{ background: 'var(--odin-surface-hover)' }}>
      {Array.from({ length: columns }).map((_, i) => (
        <SkeletonText
          key={i}
          $width={`${100 / columns - 5}%`}
          $size="sm"
          style={{ marginBottom: 0 }}
        />
      ))}
    </TableRowSkeletonWrapper>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={i} columns={columns} />
    ))}
  </div>
));

TableSkeleton.displayName = 'TableSkeleton';
TableSkeleton.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
};

/**
 * Widget Grid Skeleton (for dashboard layout)
 */
const WidgetGridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const WidgetGridSkeleton = memo(({ count = 4 }) => (
  <WidgetGridWrapper>
    {Array.from({ length: count }).map((_, i) => (
      <DataCardSkeleton key={i} rows={3} />
    ))}
  </WidgetGridWrapper>
));

WidgetGridSkeleton.displayName = 'WidgetGridSkeleton';
WidgetGridSkeleton.propTypes = {
  count: PropTypes.number,
};

/**
 * Stats Row Skeleton (for quick stats)
 */
const StatsRowWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

export const StatsRowSkeleton = memo(({ count = 6 }) => (
  <StatsRowWrapper>
    {Array.from({ length: count }).map((_, i) => (
      <StatCardSkeleton key={i} />
    ))}
  </StatsRowWrapper>
));

StatsRowSkeleton.displayName = 'StatsRowSkeleton';
StatsRowSkeleton.propTypes = {
  count: PropTypes.number,
};

/**
 * Chart Skeleton
 */
const ChartSkeletonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: ${({ $height }) => $height || '200px'};
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ChartBar = styled(SkeletonBase)`
  flex: 1;
  max-width: 40px;
  height: ${({ $height }) => $height};
  border-radius: ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0 0;
`;

export const ChartSkeleton = memo(({ bars = 7, height = '200px' }) => (
  <ChartSkeletonWrapper $height={height}>
    {Array.from({ length: bars }).map((_, i) => (
      <ChartBar key={i} $height={`${30 + Math.random() * 70}%`} />
    ))}
  </ChartSkeletonWrapper>
));

ChartSkeleton.displayName = 'ChartSkeleton';
ChartSkeleton.propTypes = {
  bars: PropTypes.number,
  height: PropTypes.string,
};

/**
 * Health Score Skeleton (circle progress)
 */
const HealthScoreSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const HealthScoreSkeleton = memo(() => (
  <HealthScoreSkeletonWrapper>
    <SkeletonCircle $size="120px" />
    <SkeletonText $width="100px" />
  </HealthScoreSkeletonWrapper>
));

HealthScoreSkeleton.displayName = 'HealthScoreSkeleton';

/**
 * List Item Skeleton
 */
const ListItemSkeletonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

const ListItemContent = styled.div`
  flex: 1;
`;

export const ListItemSkeleton = memo(({ showAvatar = false }) => (
  <ListItemSkeletonWrapper>
    {showAvatar && <SkeletonCircle $size="36px" />}
    <ListItemContent>
      <SkeletonText $width="80%" />
      <SkeletonText $width="50%" $size="sm" />
    </ListItemContent>
  </ListItemSkeletonWrapper>
));

ListItemSkeleton.displayName = 'ListItemSkeleton';
ListItemSkeleton.propTypes = {
  showAvatar: PropTypes.bool,
};

/**
 * Full Dashboard Page Skeleton
 */
export const DashboardSkeleton = memo(() => (
  <div style={{ padding: '24px' }}>
    {/* Header */}
    <div style={{ marginBottom: '24px' }}>
      <SkeletonText $width="300px" $size="xl" />
      <SkeletonText $width="200px" $size="sm" />
    </div>

    {/* Stats Row */}
    <div style={{ marginBottom: '32px' }}>
      <StatsRowSkeleton count={6} />
    </div>

    {/* Section Title */}
    <SkeletonText $width="150px" $size="lg" style={{ marginBottom: '16px' }} />

    {/* Health Widget */}
    <div style={{ marginBottom: '32px' }}>
      <DataCardSkeleton minHeight="180px" rows={3} />
    </div>

    {/* Section Title */}
    <SkeletonText $width="180px" $size="lg" style={{ marginBottom: '16px' }} />

    {/* Widget Grid */}
    <WidgetGridSkeleton count={4} />
  </div>
));

DashboardSkeleton.displayName = 'DashboardSkeleton';

export default {
  SkeletonBlock,
  SkeletonText,
  SkeletonCircle,
  StatCardSkeleton,
  DataCardSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  WidgetGridSkeleton,
  StatsRowSkeleton,
  ChartSkeleton,
  HealthScoreSkeleton,
  ListItemSkeleton,
  DashboardSkeleton,
};
